// src/hooks/usePurchaseOrders.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PurchaseOrderService } from '../admin/pages/purchase-orders/purchaseOrderService';
import { PurchaseOrderFormData, PurchaseOrderStatus } from '../admin/pages/purchase-orders/PurchaseOrder';

export const PO_KEYS = {
  all: ['purchaseOrders'] as const,
  lists: () => ['purchaseOrders', 'list'] as const,
  paginated: (page: number, search: string, status: string, payment: string) =>
    ['purchaseOrders', 'list', page, search, status, payment] as const,
  stats: () => ['purchaseOrders', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface POPaginationParams {
  page: number;
  search: string;
  status: string;
  payment: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getPurchaseOrdersPaginated instead
// of cursor objects. Any page can now be fetched in any order.
export const usePurchaseOrdersPaginated = (params: POPaginationParams) => {
  return useQuery({
    queryKey: PO_KEYS.paginated(params.page, params.search, params.status, params.payment),
    queryFn: () =>
      PurchaseOrderService.getPurchaseOrdersPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        params.status !== 'all' ? (params.status as PurchaseOrderStatus) : undefined,
        params.payment !== 'all' ? (params.payment as any) : undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

export const usePOStats = () => {
  return useQuery({
    queryKey: PO_KEYS.stats(),
    queryFn: PurchaseOrderService.getPOStats.bind(PurchaseOrderService),
    staleTime: 1000 * 60 * 5,
  });
};

export const usePOMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: PO_KEYS.all });
  };

  const create = useMutation({
    mutationFn: ({
      formData,
      userId,
      userName,
    }: {
      formData: PurchaseOrderFormData;
      userId: string;
      userName: string;
    }) => PurchaseOrderService.createPurchaseOrder(formData, userId, userName),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: ({
      poId,
      formData,
    }: {
      poId: string;
      formData: PurchaseOrderFormData;
    }) => PurchaseOrderService.updatePurchaseOrder(poId, formData),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (poId: string) => PurchaseOrderService.deletePurchaseOrder(poId),
    onSuccess: invalidateAll,
  });

  const updateStatus = useMutation({
    mutationFn: ({
      poId,
      status,
      userId,
      userName,
    }: {
      poId: string;
      status: PurchaseOrderStatus;
      userId: string;
      userName: string;
    }) => PurchaseOrderService.updatePOStatus(poId, status, userId, userName),
    onSuccess: invalidateAll,
  });

  const updatePayment = useMutation({
    mutationFn: ({ poId, paidAmount }: { poId: string; paidAmount: number }) =>
      PurchaseOrderService.updatePaymentStatus(poId, paidAmount),
    onSuccess: invalidateAll,
  });

  const markReceived = useMutation({
    mutationFn: ({
      poId,
      items,
      userId,
      userName,
    }: {
      poId: string;
      items: any[];
      userId: string;
      userName: string;
    }) => PurchaseOrderService.markPOAsReceivedWithInventoryUpdate(poId, items, userId, userName),
    onSuccess: invalidateAll,
  });

  return { create, update, remove, updateStatus, updatePayment, markReceived };
};